import type { TaskStatus } from "../data/types";

const VARS: Record<TaskStatus, string> = {
  done: "var(--done)",
  failed: "var(--failed)",
  "needs-input": "var(--needs-input)",
  pending: "var(--pending)",
  provisioning: "var(--running)",
  review: "var(--review)",
  running: "var(--running)",
};

/** CSS custom-property reference for a status color, e.g. "var(--running)". */
export const statusColorVar = (s: TaskStatus): string => VARS[s];

/**
 * The task has stopped for good — nothing further will happen to it without the
 * user acting. Drives the Done column's membership and the archive affordance,
 * both of which apply to a failure just as much as to a success.
 */
export const isTerminal = (s: TaskStatus): boolean =>
  s === "done" || s === "failed";

/**
 * The agent is no longer working: it asked a question, ended a turn, finished,
 * or gave up. A superset of `isTerminal` — every terminal status has stopped,
 * and `needs-input` and `review` have stopped without being over.
 *
 * Written as the states it excludes rather than the four it admits, because
 * "the agent is working" is the closed set: a task is either waiting for its
 * sandbox or being worked on, or it has stopped and something now wants a
 * human. New statuses join on the stopped side, and `status.test.ts` is
 * exhaustive over `TaskStatus` so one cannot be added without saying which side
 * it belongs on.
 *
 * `provisioning` is excluded for totality, not because it can occur: the
 * dashboard's `TaskStatus` carries it, but `mapTask` never produces it and
 * agents-and-tasks.md §State machine is explicit that provisioning is a
 * sub-state of `pending` rather than a status. It sits on the working side
 * because that is what it would mean.
 *
 * This is what the tab badge is keyed on (`chrome/useFaviconAttention.ts`).
 */
export const hasStopped = (s: TaskStatus): boolean =>
  s !== "pending" && s !== "provisioning" && s !== "running";
